/**
 * Multiplayer presence for shared AI chats.
 *
 * Chats are stored centrally and shared across all collaborators on a file.
 * This module broadcasts the local user's activity on the current chat
 * (processing / queued / idle) over the multiplayer presence channel, and
 * reacts to collaborators' activity by refetching the transcript when someone
 * else finishes a turn — so everyone sees the queue and the resulting output in
 * near real time.
 *
 * Presence is ephemeral (dropped on disconnect); the durable transcript lives
 * in the API/Postgres.
 */

import {
  aiChatTypingAtom,
  chatsAtom,
  collaboratorProcessingCurrentChatAtom,
  collaboratorTranscriptPendingCurrentChatAtom,
  currentChatBaseAtom,
  loadingAtom,
  promptQueueAtom,
  showAIAnalystAtom,
  showChatHistoryAtom,
} from '@/app/ai/atoms/aiAnalystAtoms/primitives';
import { aiStore } from '@/app/ai/atoms/aiAnalystAtoms/store';
import type { QueuedPrompt } from '@/app/ai/atoms/aiAnalystAtoms/types';
import { aiAnalystServerChats } from '@/app/ai/offline/aiAnalystServerChats';
import { mergeChatLists, transcriptLength } from '@/app/ai/utils/mergeChatLists';
import { debugFlag } from '@/app/debugFlags/debugFlags';
import { events } from '@/app/events/events';
import { multiplayer } from '@/app/web-workers/multiplayerWebWorker/multiplayer';
import type { AiChatPresence } from '@/app/web-workers/multiplayerWebWorker/multiplayerTypes';
import { apiClient } from '@/shared/api/apiClient';
import { trackEvent } from '@/shared/utils/analyticsEvents';
import { isContentText } from 'quadratic-shared/ai/helpers/message.helper';
import type { Chat } from 'quadratic-shared/typesAndSchemasAI';

const getQueuePreview = (item: QueuedPrompt): string => {
  const text = item.content
    .filter(isContentText)
    .map((t) => t.text)
    .join(' ');
  return text.length > 100 ? `${text.slice(0, 100)}…` : text;
};

// Dedupes identical consecutive presence broadcasts.
let lastSentKey: string | undefined;

// A stable submitted_at for the current non-idle "episode" (a run with the same
// chat + status). Re-asserts (heartbeat / join) and prompt-preview changes must
// not bump it, or the collaborator queue would keep reordering by submitted_at.
let episodeBase: string | undefined;
let episodeSubmittedAt: number | undefined;

const presenceKey = (presence: AiChatPresence): string =>
  `${presence.chat_id}:${presence.status}:${presence.prompt_preview ?? ''}:${presence.message_count ?? ''}`;

// The transcript length this client would produce on the server, i.e. the count
// observers will see after they fetch. Uses the shared `transcriptLength` so the
// advertised and fetched counts line up.
const localTranscriptLength = (): number => transcriptLength(aiStore.get(currentChatBaseAtom));

// This client's presence on the active chat, or undefined when there is nothing
// to broadcast — a brand-new unsaved chat, or a passive (idle/typing) presence
// while the AI panel is closed.
//
// "Present on a chat" means the AI panel is open on it, OR there is active work
// (a processing/queued turn) that collaborators still need to see even if the
// panel was closed mid-turn. A passive presence with the panel closed is
// suppressed so collaborators don't see "…is viewing" for a chat the user no
// longer has open.
//
// Chats follow file access, so presence (status + transcript count) is shared
// with everyone on the file — this is what keeps turn-taking and result-sync
// working across collaborators.
const computeLocalPresence = (): AiChatPresence | undefined => {
  const chat = aiStore.get(currentChatBaseAtom);
  const chatId = chat.id;
  if (!chatId) return undefined;

  const loading = aiStore.get(loadingAtom);
  const queue = aiStore.get(promptQueueAtom);
  const typing = aiStore.get(aiChatTypingAtom);
  const panelOpen = aiStore.get(showAIAnalystAtom);

  let status: AiChatPresence['status'];
  let promptPreview: string | undefined;
  if (loading) {
    status = 'processing';
  } else if (queue.length > 0) {
    status = 'queued';
    promptPreview = getQueuePreview(queue[0]);
  } else if (typing) {
    status = 'typing';
  } else {
    status = 'idle';
  }

  // With the panel closed, only active work (processing/queued) stays visible so
  // turn-taking and result-sync keep working; passive presence is suppressed.
  if (!panelOpen && status !== 'processing' && status !== 'queued') return undefined;

  const presence: AiChatPresence = { chat_id: chatId, status, message_count: localTranscriptLength() };
  if (promptPreview) presence.prompt_preview = promptPreview;
  if (status !== 'idle') {
    const base = `${chatId}:${status}`;
    if (base !== episodeBase) {
      episodeBase = base;
      episodeSubmittedAt = Date.now();
    }
    presence.submitted_at = episodeSubmittedAt;
  } else {
    episodeBase = undefined;
    episodeSubmittedAt = undefined;
  }
  return presence;
};

// Clear our presence for everyone (and our own other sessions) — used when the
// panel closes with no active turn, or we drop to a state with nothing to
// broadcast. Idempotent: only sends if we had something out there.
const clearLocalAIChatPresence = (): void => {
  if (lastSentKey === undefined) return;
  lastSentKey = undefined;
  episodeBase = undefined;
  episodeSubmittedAt = undefined;
  multiplayer.clearAIChatPresence();
};

export const broadcastAIChatPresence = (): void => {
  const presence = computeLocalPresence();
  if (!presence) {
    // Transitioned to a state with nothing to broadcast (e.g. a turn finished
    // with the panel closed, or we switched to a new unsaved chat) after having
    // broadcast something. Clear so collaborators don't see stale presence — and
    // so a stuck "processing" doesn't freeze turn-taking on their side.
    clearLocalAIChatPresence();
    return;
  }

  const key = presenceKey(presence);
  if (key === lastSentKey) return;
  lastSentKey = key;

  multiplayer.sendAIChatPresence(presence);
};

// Re-send the current presence even if it is unchanged. Used to (a) inform a
// session that joined after our last (deduped) broadcast, and (b) heal a dropped
// presence delta.
//
// Non-idle presence is always re-asserted. Idle presence is re-asserted only
// while there are other sessions to re-sync to (never in a solo session) — this
// heals a dropped *completion* delta (the final idle+count sent when a turn
// finishes) so the running window's peers reach the final transcript.
const reassertAIChatPresence = (): void => {
  const presence = computeLocalPresence();
  if (!presence) return;
  if (presence.status === 'idle') {
    if (presence.message_count === undefined) return;
    if (!multiplayer.getUsers().some((user) => user.session_id !== 'ai-analyst')) return;
  }
  lastSentKey = presenceKey(presence);
  multiplayer.sendAIChatPresence(presence);
};

const refetchCurrentChat = async (fileUuid: string, chatId: string): Promise<void> => {
  // Don't clobber a turn we're running locally.
  if (aiStore.get(loadingAtom)) return;
  try {
    const { chat } = await apiClient.ai.chats.get(fileUuid, chatId);
    // The shared transcript only ever grows. Retries and out-of-order responses
    // must never let a shorter (older) fetch replace a longer one we already hold.
    // Compare with the same filter the server persists / advertises so a local
    // transcript carrying non-persisted context messages (raw length > filtered)
    // doesn't wrongly reject a legitimate server update.
    const fetchedLength = transcriptLength(chat);
    const isNewer = (prev: Chat | undefined): boolean => !prev || fetchedLength >= transcriptLength(prev);

    aiStore.set(chatsAtom, (prev) => {
      const existing = prev.find((c) => c.id === chat.id);
      if (existing && !isNewer(existing)) return prev;
      return [...prev.filter((c) => c.id !== chat.id), chat];
    });
    const currentChat = aiStore.get(currentChatBaseAtom);
    if (currentChat.id === chat.id && isNewer(currentChat)) {
      aiStore.set(currentChatBaseAtom, chat);
    }
  } catch {
    // Best effort; the next signal or hydration will reconcile.
  }
};

// Reconcile the chat list against the server (source of truth) so chats created,
// renamed, or deleted by collaborators appear/update/disappear without a refresh.
// Shares mergeChatLists with initialization so a live refresh and a reload
// produce the same list.
const mergeServerChats = (serverChats: Chat[]): void => {
  const current = aiStore.get(currentChatBaseAtom);
  const mergedList = mergeChatLists(serverChats, current.id ? current : undefined);
  aiStore.set(chatsAtom, mergedList);

  // Keep the open panel on the same chat the list settled on: longer transcript
  // wins (so a collaborator's completed turn appears without a refresh), and
  // otherwise local messages with server name/author.
  if (!current.id) return;
  const mergedOpen = mergedList.find((chat) => chat.id === current.id);
  if (
    mergedOpen &&
    (mergedOpen.name !== current.name ||
      mergedOpen.author?.email !== current.author?.email ||
      transcriptLength(mergedOpen) !== transcriptLength(current))
  ) {
    aiStore.set(currentChatBaseAtom, mergedOpen);
  }
};

// Guards against overlapping list refreshes (presence can fire rapidly).
let refreshingChatList = false;
const refreshChatList = async (): Promise<void> => {
  if (refreshingChatList || !aiAnalystServerChats.fileUuid) return;
  refreshingChatList = true;
  try {
    mergeServerChats(await aiAnalystServerChats.loadChats());
  } catch {
    // Best effort; the next signal or panel open reconciles.
  } finally {
    refreshingChatList = false;
  }
};

// A collaborator is active on a chat we've never seen — someone started a new
// chat. Pull the list so it shows up live.
const hasUnknownCollaboratorChat = (): boolean => {
  const known = new Set(aiStore.get(chatsAtom).map((chat) => chat.id));
  const currentId = aiStore.get(currentChatBaseAtom).id;
  return multiplayer.getUsers().some((user) => {
    const chatId = user.ai_chat?.chat_id;
    return !!chatId && chatId !== currentId && !known.has(chatId);
  });
};

const anyCollaboratorProcessing = (chatId: string): boolean =>
  !!chatId &&
  multiplayer.getUsers().some((user) => user.ai_chat?.chat_id === chatId && user.ai_chat.status === 'processing');

// Our local transcript length for a chat, measured the same way
// `localTranscriptLength` advertises it — otherwise advertised (filtered) and
// local (raw) counts drift and sync over/undershoots.
const currentMessageCount = (chatId: string): number => {
  const chat = aiStore.get(currentChatBaseAtom);
  return chat.id === chatId ? transcriptLength(chat) : 0;
};

// Longest transcript any collaborator has advertised for a chat via presence.
// This is the signal that replaces polling: when it exceeds what we hold, the
// transcript changed and we fetch.
const maxAdvertisedCount = (chatId: string): number => {
  let max = 0;
  for (const user of multiplayer.getUsers()) {
    const presence = user.ai_chat;
    if (presence && presence.chat_id === chatId && typeof presence.message_count === 'number') {
      if (presence.message_count > max) max = presence.message_count;
    }
  }
  return max;
};

// A collaborator advertises a transcript length the instant their local
// transcript grows — which is slightly before the server commits that write
// (the server streams, then bills, then persists). So fetch immediately and, if
// the server hasn't caught up to `target` yet, retry a bounded number of times.
// No steady-state polling: this runs only while catching up to a real change.
const SYNC_RETRY_MS = 1500;
const SYNC_MAX_ATTEMPTS = 12;
let syncTimeout: ReturnType<typeof setTimeout> | undefined;

const clearSync = (): void => {
  if (syncTimeout !== undefined) {
    clearTimeout(syncTimeout);
    syncTimeout = undefined;
  }
};

// True while actively fetching to reach a collaborator's advertised transcript
// length. Gates turn-taking: a queued turn must not start until we hold the
// collaborator's just-finished turn, or it would run on a stale base and get
// rejected/overwritten by the server's monotonic guard. Cleared when we catch up
// or exhaust retries (so an unreachable target never deadlocks the queue).
const setTranscriptPending = (pending: boolean): void => {
  aiStore.set(collaboratorTranscriptPendingCurrentChatAtom, pending);
};

const syncToTarget = (fileUuid: string, chatId: string, target: number): void => {
  clearSync();
  setTranscriptPending(true);
  const startedAt = Date.now();
  const attempt = async (n: number): Promise<void> => {
    if (aiStore.get(currentChatBaseAtom).id !== chatId) {
      setTranscriptPending(false);
      return;
    }
    await refetchCurrentChat(fileUuid, chatId);
    const fetched = currentMessageCount(chatId);
    if (fetched >= target) {
      // `fetched > target` is benign: the transcript simply advanced further
      // (concurrent activity) between picking the target and fetching. Only
      // undershoot below indicates real advertised/fetched misalignment.
      if (debugFlag('debugAIChatSync')) {
        console.log(
          `[AIChatSync] ${fetched === target ? 'ok' : 'ahead'} chat=${chatId} target=${target} fetched=${fetched} attempts=${n + 1} ms=${Date.now() - startedAt}`
        );
      }
      setTranscriptPending(false);
      return; // caught up
    }
    if (n + 1 < SYNC_MAX_ATTEMPTS && aiStore.get(currentChatBaseAtom).id === chatId) {
      syncTimeout = setTimeout(() => void attempt(n + 1), SYNC_RETRY_MS);
      return;
    }
    // Exhausted retries without reaching the advertised count. Either the
    // server write never landed, or advertised/fetched counts are misaligned.
    // Clear the gate regardless so the queue is never stuck behind an
    // unreachable target.
    setTranscriptPending(false);
    if (debugFlag('debugAIChatSync')) {
      console.warn(
        `[AIChatSync] undershoot chat=${chatId} target=${target} fetched=${fetched} attempts=${n + 1} ms=${Date.now() - startedAt}`
      );
    }
    trackEvent('[AIAnalyst].transcriptSyncDrift', { kind: 'undershoot', target, fetched, attempts: n + 1 });
  };
  void attempt(0);
};

/**
 * Recompute whether any collaborator is currently processing a turn on the
 * current chat, so this client can gate its own turns (turn-taking).
 */
export const updateCollaboratorProcessingState = (): void => {
  const chatId = aiStore.get(currentChatBaseAtom).id;
  aiStore.set(collaboratorProcessingCurrentChatAtom, anyCollaboratorProcessing(chatId));
};

/**
 * Fetch the current chat's transcript iff a collaborator has advertised a longer
 * one than we hold. Driven by presence changes (a collaborator's message_count
 * growing) and by switching chats — never by a timer.
 */
export const syncCurrentChatFromPresence = (): void => {
  const chatId = aiStore.get(currentChatBaseAtom).id;
  const fileUuid = aiAnalystServerChats.fileUuid;
  if (!chatId || !fileUuid) {
    setTranscriptPending(false);
    return;
  }
  const target = maxAdvertisedCount(chatId);
  if (target > currentMessageCount(chatId)) {
    syncToTarget(fileUuid, chatId, target);
  } else {
    // We already hold everything collaborators have advertised; nothing to catch
    // up to, so a queued turn is safe to release.
    clearSync();
    setTranscriptPending(false);
  }
};

const handleCollaboratorPresence = (): void => {
  updateCollaboratorProcessingState();
  syncCurrentChatFromPresence();
  // A collaborator working in a chat we don't have yet means a new chat exists;
  // pull the list so it appears without a refresh.
  if (hasUnknownCollaboratorChat()) {
    void refreshChatList();
  }
};

// Re-assert non-idle presence on a fixed cadence so a dropped presence delta
// self-heals rather than leaving a collaborator's view stuck.
const PRESENCE_HEARTBEAT_MS = 5000;

// How long after the last keystroke the local user is still considered "typing".
const TYPING_IDLE_MS = 2500;
let typingTimeout: ReturnType<typeof setTimeout> | undefined;

/**
 * Signal that the local user is composing a prompt in the current chat. Broadcast
 * as `typing` presence and auto-cleared after a short idle period. Call on each
 * keystroke in the chat composer.
 */
export const signalAIChatTyping = (): void => {
  aiStore.set(aiChatTypingAtom, true);
  if (typingTimeout) clearTimeout(typingTimeout);
  typingTimeout = setTimeout(() => aiStore.set(aiChatTypingAtom, false), TYPING_IDLE_MS);
};

// Session ids of collaborators last seen in the room (excluding this client and
// the client-managed AI user), used to detect a newcomer.
let knownSessionIds = new Set<string>();

// When a collaborator joins the room they missed our last (deduped) broadcast,
// so re-assert our current presence for their benefit.
const handleRoomMembership = (): void => {
  const current = new Set(
    multiplayer
      .getUsers()
      .map((user) => user.session_id)
      .filter((sessionId) => sessionId !== 'ai-analyst')
  );
  let joined = false;
  for (const sessionId of current) {
    if (!knownSessionIds.has(sessionId)) {
      joined = true;
      break;
    }
  }
  knownSessionIds = current;
  if (joined) reassertAIChatPresence();
};

// Emits collaboration metrics around the local turn lifecycle: how many
// collaborators were active when a turn started (contention) and how long the
// turn took. Driven by loadingAtom transitions.
let turnStartedAt: number | undefined;
const trackTurnMetrics = (): void => {
  const loading = aiStore.get(loadingAtom);
  if (loading && turnStartedAt === undefined) {
    turnStartedAt = Date.now();
    const chatId = aiStore.get(currentChatBaseAtom).id;
    trackEvent('[AIAnalyst].turnStarted', {
      collaboratorsActive: chatId ? getChatCollaborators(chatId).length : 0,
    });
  } else if (!loading && turnStartedAt !== undefined) {
    trackEvent('[AIAnalyst].turnCompleted', { durationMs: Date.now() - turnStartedAt });
    turnStartedAt = undefined;
  }
};

let installed = false;

/**
 * Wire up presence broadcasting and collaborator-driven refetching. Safe to
 * call multiple times; only the first call installs the subscriptions.
 */
export const installAIChatPresence = (): void => {
  if (installed) return;
  installed = true;
  aiStore.sub(loadingAtom, broadcastAIChatPresence);
  aiStore.sub(currentChatBaseAtom, broadcastAIChatPresence);
  aiStore.sub(promptQueueAtom, broadcastAIChatPresence);
  aiStore.sub(aiChatTypingAtom, broadcastAIChatPresence);
  // Presence is only shown while the panel is open (plus active turns): opening
  // announces our presence; closing clears the passive "…is viewing" marker.
  aiStore.sub(showAIAnalystAtom, () => {
    if (aiStore.get(showAIAnalystAtom)) {
      reassertAIChatPresence();
    } else {
      broadcastAIChatPresence();
    }
  });
  aiStore.sub(loadingAtom, trackTurnMetrics);
  aiStore.sub(currentChatBaseAtom, updateCollaboratorProcessingState);
  // Switching chats should pull any transcript a collaborator has advertised.
  aiStore.sub(currentChatBaseAtom, syncCurrentChatFromPresence);
  // Opening the history panel reconciles the list (renames/deletes by others).
  aiStore.sub(showChatHistoryAtom, () => {
    if (aiStore.get(showChatHistoryAtom)) void refreshChatList();
  });
  events.on('multiplayerAIChatPresence', handleCollaboratorPresence);
  // Collaborators leaving the room (e.g. disconnect mid-turn) also frees the chat.
  events.on('multiplayerUpdate', updateCollaboratorProcessingState);
  // Re-assert presence for collaborators who join after our last broadcast.
  events.on('multiplayerUpdate', handleRoomMembership);
  setInterval(reassertAIChatPresence, PRESENCE_HEARTBEAT_MS);
};

/**
 * Collaborators (other users) currently active on the given chat, newest first.
 */
export const getChatCollaborators = (chatId: string): Array<{ sessionId: string; presence: AiChatPresence }> => {
  if (!chatId) return [];
  return multiplayer
    .getUsers()
    .filter((user) => user.ai_chat && user.ai_chat.chat_id === chatId && user.ai_chat.status !== 'idle')
    .map((user) => ({ sessionId: user.session_id, presence: user.ai_chat as AiChatPresence }))
    .sort((a, b) => (a.presence.submitted_at ?? 0) - (b.presence.submitted_at ?? 0));
};
